/**
 * ProcessoFilterPanel Component
 *
 * Story: 11.5 - Processos Atribuídos
 * Filter panel for processo list
 */
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Filter, X } from "lucide-react";
import {
  ProcessoFilterPanelProps,
  ProcessoFilterParams,
  ProcessoStatus,
  ProcessoPrioridade,
  PROCESSO_STATUS_LABELS,
  PRIORIDADE_LABELS,
} from "../../types/processo-parceiro";

const ProcessoFilterPanel: React.FC<ProcessoFilterPanelProps> = ({
  onFilterChange,
  initialFilters = {},
}) => {
  const [filters, setFilters] = useState<ProcessoFilterParams>(initialFilters);

  // Apply filters with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      onFilterChange(filters);
    }, 300);

    return () => clearTimeout(timer);
  }, [filters, onFilterChange]);

  const handleStatusChange = (value: string) => {
    setFilters((prev) => ({
      ...prev,
      status: value && value !== "_all_" ? (value as ProcessoStatus) : undefined,
    }));
  };

  const handlePrioridadeChange = (value: string) => {
    setFilters((prev) => ({
      ...prev,
      prioridade: value && value !== "_all_" ? (value as ProcessoPrioridade) : undefined,
    }));
  };

  const handlePrazoVencidoChange = (checked: boolean) => {
    setFilters((prev) => ({
      ...prev,
      prazo_vencido: checked ? true : undefined,
    }));
  };

  const handleClearFilters = () => {
    setFilters({});
    onFilterChange({});
  };

  const hasActiveFilters = Object.keys(filters).some(
    (key) => filters[key as keyof ProcessoFilterParams] !== undefined
  );

  return (
    <Card className="p-4 mb-6">
      <div className="flex flex-wrap items-center gap-4">
        {/* Filter Icon */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">Filtros</span>
        </div>

        {/* Status Filter */}
        <div className="w-[200px]">
          <Select value={filters.status || "_all_"} onValueChange={handleStatusChange}>
            <SelectTrigger>
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all_">Todos</SelectItem>
              {Object.values(ProcessoStatus).map((status) => (
                <SelectItem key={status} value={status}>
                  {PROCESSO_STATUS_LABELS[status]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Prioridade Filter */}
        <div className="w-[180px]">
          <Select value={filters.prioridade || "_all_"} onValueChange={handlePrioridadeChange}>
            <SelectTrigger>
              <SelectValue placeholder="Prioridade" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all_">Todas</SelectItem>
              {Object.values(ProcessoPrioridade).map((prioridade) => (
                <SelectItem key={prioridade} value={prioridade}>
                  {PRIORIDADE_LABELS[prioridade]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Prazo Vencido Checkbox */}
        <div className="flex items-center space-x-2">
          <Checkbox
            id="prazo-vencido"
            checked={filters.prazo_vencido || false}
            onCheckedChange={handlePrazoVencidoChange}
          />
          <Label htmlFor="prazo-vencido" className="text-sm font-normal cursor-pointer">
            Apenas com prazo vencido
          </Label>
        </div>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <Button variant="outline" size="sm" onClick={handleClearFilters} className="ml-auto">
            <X className="w-4 h-4 mr-2" />
            Limpar Filtros
          </Button>
        )}
      </div>
    </Card>
  );
};

export default ProcessoFilterPanel;
